import React, { Fragment, useState, useEffect } from "react";
import DebounceInput from "react-debounce-input";
import classNames from "classnames";
import { dateHelpers } from "../utils";
import { toast } from "react-toastify";

// Reject years below this floor. The backend narrows date filters into a T-SQL
// `datetime` local (floor 1753-01-01); a pre-1753 value throws a datetime2->datetime
// conversion error (Log #34153). 1900 is a business-meaningful floor well clear of that.
const MIN_DATE_YEAR = 1900;

// A <input type=date> produces a COMPLETE value (e.g. 0001-01-01) after the first digit of the
// year, so while a 4-digit year is still being typed we transiently see years below 1000. Treat
// those as an unfinished entry rather than a real out-of-range date (avoids a false warning and,
// combined with never overwriting the input, avoids clobbering month/day mid-entry).
const MIN_COMPLETE_YEAR = 1000;

export default function FilterDateTime(props) {
   // Keep the input's own value a string ("" when empty) so it stays a controlled
   // input; the filter callback still reports null upward to mean "no filter".
   const [value, setValue] = useState(props.value ?? "");

   useEffect(() => {
      setValue(props.value ?? "");
   }, [props.value]);

   function commitValue(newValue, filteredDisplay) {
      props.onChangeCallback({
         filterName: props.filterName,
         value: newValue,
         filteredDisplay,
      });
      setValue(newValue ?? "");
   }

   // Reject an out-of-range/invalid entry WITHOUT committing: no onChangeCallback fires (so
   // live-search consumers never submit on a bad date), but we keep the local value in sync with
   // what was typed. We must NEVER overwrite the input here — doing so wiped the month/day out
   // from under the user while they were still typing the year.
   function rejectEntry(entered, warning) {
      setValue(entered);
      if (warning) toast.warning(warning);
   }

   function onChange(e) {
      const entered = e.target.value;

      // A type=date input reports "" until month, day, AND year are all filled, so an empty value
      // here is either a fresh in-progress entry (do nothing — avoid a spurious query mid-typing)
      // or a real clear of a committed date (commit null to remove the filter). Only the latter
      // should submit.
      if (!entered) {
         if (props.value) commitValue(null, null);
         else setValue("");
         return;
      }

      const parsed = dateHelpers.verifyDate(entered);
      if (!parsed)
         return rejectEntry(entered, "Please enter a valid date");
      if (parsed.year() < MIN_DATE_YEAR) {
         // Silent while a 4-digit year is still being typed (year < 1000); warn only once a full
         // year has landed below the floor.
         const stillTypingYear = parsed.year() < MIN_COMPLETE_YEAR;
         return rejectEntry(entered, stillTypingYear ? null : `Please enter a date on or after 01/01/${MIN_DATE_YEAR}`);
      }

      const filteredDisplay = `${props.displayName} ${dateHelpers.dateTimeFormat(
         entered,
         dateHelpers.MDYHMA
      )}`;
      commitValue(entered, filteredDisplay);
   }

   return (
      <Fragment>
         {props.displayName ? (
            <div className="filter-definition-caption">{props.displayName}</div>
         ) : null}
         <DebounceInput
            className={classNames("form-control", props.className)}
            type="date"
            min={`${MIN_DATE_YEAR}-01-01`}
            onChange={onChange}
            value={value}
            debounceTimeout={500}
         />
      </Fragment>
   );
}
